import { useState } from "react";
import { Copy, CreditCard, ImageUp, Loader2, QrCode } from "lucide-react";
import { load } from "@cashfreepayments/cashfree-js";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useApp } from "@/context/AppContext";
import { api, errMsg, mediaSrc } from "@/lib/api";

function OnlinePay({ booking }) {
  const { settings, t } = useApp();
  const [busy, setBusy] = useState(false);
  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/bookings/${booking.id}/cashfree-order`, {
        token: booking.token,
        return_url: window.location.origin + "/",
      });
      const cashfree = await load({ mode: data.mode });
      const res = await cashfree.checkout({ paymentSessionId: data.payment_session_id, redirectTarget: "_self" });
      if (res?.error) throw new Error(res.error.message);
    } catch (e) {
      toast.error(e.message && !e.response ? e.message : errMsg(e));
      setBusy(false);
    }
  };
  if (!settings.cashfree_enabled) return null;
  return (
    <div className="rounded-2xl border border-emerald-300/25 bg-emerald-400/[0.06] p-4 sm:p-5" data-testid="pay-online-card">
      <p className="flex items-center gap-2 font-display text-lg text-white"><CreditCard className="h-5 w-5 text-emerald-300" /> {t("payOnline")}</p>
      <p className="mt-1 text-xs text-emerald-100/70">{t("payOnlineSub")}</p>
      <Button data-testid="pay-online-btn" disabled={busy} onClick={pay} className="mt-4 h-11 w-full rounded-full bg-emerald-400 text-emerald-950 hover:bg-emerald-300">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `${t("payNow")} Rs ${booking.amount}`}
      </Button>
    </div>
  );
}

function UpiPay({ booking, onDone, allowSkip = true }) {
  const { settings, t } = useApp();
  const [utr, setUtr] = useState("");
  const [proof, setProof] = useState(null);
  const [busy, setBusy] = useState(false);
  const [skipBusy, setSkipBusy] = useState(false);
  const qrImg = mediaSrc(settings.media.qr);
  const hasPay = qrImg || settings.upi_id;
  const utrOk = utr.trim().length >= 6;
  const canSubmit = !!proof || utrOk;

  const copyUpi = async () => {
    try {
      await navigator.clipboard.writeText(settings.upi_id);
      toast.success(t("upiCopied"));
    } catch {
      toast.error(settings.upi_id);
    }
  };

  const submit = async () => {
    if (!canSubmit) { toast.error(t("proofRequired")); return; }
    setBusy(true);
    try {
      if (proof) {
        const fd = new FormData();
        fd.append("token", booking.token);
        fd.append("file", proof);
        await api.post(`/bookings/${booking.id}/payment-proof`, fd);
      }
      const { data } = await api.post(`/bookings/${booking.id}/upi-submit`, { token: booking.token, utr });
      onDone(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const skip = async () => {
    setSkipBusy(true);
    try {
      const { data } = await api.post(`/bookings/${booking.id}/skip-payment`, { token: booking.token });
      onDone(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSkipBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-amber-200/25 bg-amber-200/[0.05] p-4 sm:p-5" data-testid="pay-upi-card">
      <p className="flex items-center gap-2 font-display text-lg text-white"><QrCode className="h-5 w-5 text-amber-200" /> {t("payUpi")}</p>
      <p className="mt-1 text-xs text-emerald-100/70">{t("payUpiSub")}</p>
      <p className="mt-3 flex items-baseline justify-between gap-3 rounded-xl border border-amber-200/30 bg-black/25 px-3 py-2 text-sm" data-testid="amount-to-pay">
        <span className="text-emerald-100/70">{t("amountToPay")}</span>
        <span className="font-display text-2xl text-amber-200">Rs {booking.amount}</span>
      </p>
      {!hasPay ? (
        <p className="mt-4 text-sm text-amber-100/80" data-testid="upi-not-set">{t("upiNotSet")}</p>
      ) : (
        <div className="mt-4 flex flex-col items-center gap-3">
          <p className="rounded-xl border border-amber-200/30 bg-black/20 px-3 py-2 text-center text-xs text-amber-100/90" data-testid="upi-instructions">{t("upiInstructions")}</p>
          {qrImg && (
            <div className="rounded-2xl bg-white p-3" data-testid="upi-qr">
              <img src={qrImg} alt="UPI QR" className="h-44 w-44 object-contain" />
            </div>
          )}
          {settings.upi_id && (
            <div className="flex w-full flex-col items-center gap-2">
              <p className="text-xs text-emerald-100/70" data-testid="upi-id">{settings.upi_id} · Rs {booking.amount}</p>
              <Button type="button" variant="secondary" size="sm" data-testid="copy-upi-id-btn" onClick={copyUpi} className="rounded-full">
                <Copy className="mr-2 h-4 w-4" /> {t("copyUpiId")}
              </Button>
            </div>
          )}
        </div>
      )}
      <div className={`mt-4 space-y-2`}>
        <div className={!hasPay ? "hidden" : "space-y-2"}>
          <label className="text-xs text-emerald-100/70">{t("utrLabel")}</label>
          <Input data-testid="input-utr" value={utr} onChange={(e) => setUtr(e.target.value)} maxLength={40} className="h-11 rounded-xl border-emerald-300/20 bg-black/20 text-emerald-50" />
          <label className="text-xs text-emerald-100/70">{t("proofLabel")}</label>
          <label className="flex h-11 cursor-pointer items-center gap-2 rounded-xl border border-dashed border-emerald-300/30 bg-black/20 px-3 text-sm text-emerald-100/80 hover:border-emerald-300/60" data-testid="proof-upload-label">
            <ImageUp className="h-4 w-4 shrink-0 text-emerald-300" />
            <span className="truncate">{proof ? proof.name : t("proofPick")}</span>
            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="input-payment-proof"
              onChange={(e) => { const f = e.target.files?.[0]; if (f && f.size > 8 * 1024 * 1024) { toast.error("Max 8 MB"); return; } setProof(f || null); }} />
          </label>
          {!canSubmit && <p className="text-[11px] text-amber-100/80" data-testid="proof-required-note">{t("proofRequired")}</p>}
          <Button data-testid="submit-upi-btn" disabled={busy || skipBusy || !canSubmit || (utr.trim().length > 0 && utr.trim().length < 6)} onClick={submit} className="h-11 w-full rounded-full bg-amber-300 text-emerald-950 hover:bg-amber-200 disabled:opacity-40">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("submitPayment")}
          </Button>
          <div className="flex items-center gap-2 pt-2">
            <div className="h-px flex-1 bg-emerald-300/15" />
            <span className="text-[11px] uppercase tracking-wide text-emerald-100/40">{t("or")}</span>
            <div className="h-px flex-1 bg-emerald-300/15" />
          </div>
        </div>
        <Button type="button" variant="ghost" disabled={busy || skipBusy} onClick={skip} data-testid="skip-payment-btn"
          className={`h-11 w-full rounded-full border border-emerald-300/25 bg-black/10 text-emerald-50/90 hover:bg-emerald-300/10 ${allowSkip ? "" : "hidden"}`}>
          {skipBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("proceedNoPayment")}
        </Button>
        {allowSkip && <p className="text-center text-[11px] text-emerald-100/50">{t("proceedNoPaymentHint")}</p>}
      </div>
      <p className="mt-4 rounded-xl border border-emerald-300/15 bg-black/15 p-3 text-[11px] leading-relaxed text-emerald-100/70" data-testid="cancellation-policy">
        {t("cancellationPolicy")}
      </p>    </div>
  );
}

export default function PaymentPanel({ booking, onDone, allowSkip = true }) {
  const { settings } = useApp();
  return (
    <div className={settings.cashfree_enabled ? "grid gap-4 md:grid-cols-2" : "mx-auto max-w-md"} data-testid="payment-panel">
      <OnlinePay booking={booking} />
      <UpiPay booking={booking} onDone={onDone} allowSkip={allowSkip} />
    </div>
  );
}
